import * as React from 'react';
import { useState, useRef, useCallback } from 'react';
import { Card, Typography } from '@execonline-inc/execonline-ui';
import SparklesIcon from '../Cutesies/SparklesIcon';
import FilterIcon from '../Cutesies/FilterIcon';
import DateRangePopover from './DateRangePopover';
import { T } from '../../Translations';
import { DashboardSummaryFilters } from '../../DashboardSummaryStore/Types';
import { formatDateRange, getDefaultDateRange } from './utils';
import { useClickOutside } from './useClickOutside';

interface Props {
  message: string;
  filters: DashboardSummaryFilters;
  hadPreviousSuccess: boolean;
  onDateRangeChange?: (startDate: string, endDate: string) => void;
}

const EmptyState: React.FC<Props> = ({
  message,
  filters,
  hadPreviousSuccess,
  onDateRangeChange,
}) => {
  const [showDatePicker, setShowDatePicker] = useState(false);
  const datePickerRef = useRef<HTMLDivElement>(null);
  const settingsButtonRef = useRef<HTMLButtonElement>(null);

  const defaults = getDefaultDateRange();
  const startDate = filters.startDate.getOrElse(() => defaults.startDate);
  const endDate = filters.endDate.getOrElse(() => defaults.endDate);
  const dateRange = formatDateRange(startDate, endDate);

  const closeDatePicker = useCallback(() => setShowDatePicker(false), []);

  // Close date picker when clicking outside
  useClickOutside([datePickerRef, settingsButtonRef], showDatePicker, closeDatePicker);

  const handleDateRangeSubmit = useCallback(
    (newStartDate: string, newEndDate: string) => {
      setShowDatePicker(false);
      if (onDateRangeChange) {
        onDateRangeChange(newStartDate, newEndDate);
      }
    },
    [onDateRangeChange],
  );

  // Always show date controls when onDateRangeChange is provided
  // This allows users to adjust the date range to find data, even on first visit
  const showDateControls = !!onDateRangeChange;

  return (
    <Card
      className="w-full pt-6 pr-6 pb-6 pl-6"
      radius="lg"
      data-intercom-target="ai-summary-empty-state"
    >
      {showDateControls ? (
        <>
          {/* Header with date controls - shown when user had previous success */}
          <div className="flex items-start justify-between pb-4 mb-4 border-b border-gray-200">
            <div>
              <div className="flex items-center space-x-2">
                <SparklesIcon className="w-5 h-5" color="#6B7280" />
                <Typography variant="bodyMedium" fontWeight="semibold" className="text-gray-700">
                  <T kind="Action Plan Impact Summary" />
                </Typography>
              </div>
              {dateRange && (
                <Typography variant="caption" className="text-gray-500">
                  {dateRange}
                </Typography>
              )}
            </div>

            {/* Date picker button */}
            <div className="relative">
              <button
                ref={settingsButtonRef}
                onClick={() => setShowDatePicker(!showDatePicker)}
                className={`p-2 rounded-lg transition-colors ${
                  showDatePicker
                    ? 'bg-gray-100/30'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100/30'
                }`}
                title="Adjust date range"
                data-intercom-target="ai-summary-empty-date-filter-button"
              >
                <FilterIcon className="w-5 h-5" />
              </button>

              {showDatePicker && (
                <div ref={datePickerRef} className="absolute right-0 top-full mt-2 z-50">
                  <DateRangePopover
                    initialStartDate={startDate}
                    initialEndDate={endDate}
                    onSubmit={handleDateRangeSubmit}
                    onClose={closeDatePicker}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Empty message for date range with no data */}
          <Typography variant="bodySmall" className="text-gray-600">
            <T kind="No action plan data found for the selected date range. Try adjusting the dates to see results." />
          </Typography>
        </>
      ) : (
        /* Simple layout - shown for initial empty state */
        <div className="flex items-start space-x-3">
          <SparklesIcon className="w-5 h-5 mt-0.5" color="#6B7280" />
          <div className="flex-1">
            <Typography variant="bodyMedium" fontWeight="semibold" className="text-gray-700">
              <T kind="Action Plan Impact Summary" />
            </Typography>
            <Typography variant="bodySmall" className="mt-2 text-gray-600">
              {message}
            </Typography>
          </div>
        </div>
      )}
    </Card>
  );
};

export default EmptyState;
